import { jsPDF } from 'jspdf';

import { registerRoboto } from './fonts/registerRoboto';

import { hexToRgb } from './utils/helpers';


/* ================= CONSTANTS ================= */

const CLASS_LIST = [
  'Nursery',
  'LKG',
  'UKG',
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '10',
];

/* ================= PRINTING METHOD CONFIGS ================= */

const DIGITAL_CONFIG = {
  GRID_COLS: 5,
  GRID_ROWS: 5,
  GRID_WIDTH: 90.5,
  GRID_HEIGHT: 58,
  TOTAL_LABELS: 25,
  PDF_WIDTH: 460,
  PDF_HEIGHT: 305,
};

const OFFSET_CONFIG = {
  // Sheet dimensions are specified as 635 mm high × 457 mm wide.
  PDF_WIDTH: 457,
  PDF_HEIGHT: 635,
  GRID_WIDTH: 90.5,
  GRID_HEIGHT: 58,
  // Five existing-width columns and ten existing-height rows fit this sheet.
  GRID_COLS: 5,
  GRID_ROWS: 10,
  TOTAL_LABELS: 50,
};

const GAP = 0;

/* ================= IMAGE HELPERS ================= */

function normalizeClassKey(value) {
  return String(value || '')
    .trim()
    .toLowerCase();
}

/* ================= ROTATION ================= */

const classImageCounter = {};
const classFontCounter = {};

/* ================= MAIN ================= */

export async function generatePDF({

  orientation,

  labels,

  headerMode,

  classLabel,

  fontStyles,

  labelColors,

  lineColors,

  setGridCounts,

  setTotalGrids,

  mode,

  imageMode,
  dummyMode = false,

  printingMethod = 'digital',
  uploadedBorderImages = [],
  uploadedArtworkImages = [],

}) {

  /* ================= RESOLVE CONFIG ================= */

  const cfg =
    printingMethod === 'offset'
      ? OFFSET_CONFIG
      : DIGITAL_CONFIG;

  const GRID_COLS   = cfg.GRID_COLS;
  const GRID_ROWS   = cfg.GRID_ROWS;
  const GRID_WIDTH  = cfg.GRID_WIDTH;
  const GRID_HEIGHT = cfg.GRID_HEIGHT;
  const TOTAL_LABELS = cfg.TOTAL_LABELS;
  const PDF_WIDTH   = cfg.PDF_WIDTH;
  const PDF_HEIGHT  = cfg.PDF_HEIGHT;

  /* ================= HEADER MODE ================= */

  const resolvedHeaderMode =
    headerMode ||
    labels.headerMode ||
    'schoolName';

  const isSchoolNameMode =
    resolvedHeaderMode === 'schoolName';

  const isSchoolMode =
    resolvedHeaderMode === 'school';

  const isLogoMode =
    resolvedHeaderMode === 'logo';
  const isDummyMode = Boolean(dummyMode);

  /* ================= USER-UPLOADED BORDER IMAGES ================= */

  // The browser uploads and converts all selected files to data URLs.
  // These same embedded images are reused repeatedly for every label.
  const borderImages = uploadedBorderImages
    .map(item => item?.dataUrl || item)
    .filter(src => typeof src === 'string' && src.startsWith('data:image/'));

  const artworkImages = uploadedArtworkImages
    .map(item => item?.dataUrl || item)
    .filter(src => typeof src === 'string' && src.startsWith('data:image/'));

  // Shared Dummy Mode transform for every uploaded border/artwork image.
  const uploadedScaleX = Math.max(0.2, Number(labels.uploadedImageScaleWidth || 100) / 100);
  const uploadedScaleY = Math.max(0.2, Number(labels.uploadedImageScaleHeight || 100) / 100);
  const uploadedOffsetX = Number(labels.uploadedImageOffsetX || 0);
  const uploadedOffsetY = Number(labels.uploadedImageOffsetY || 0);

  /* ================= PDF INIT ================= */

  // Offset sheet is 457 mm wide × 635 mm high (portrait).
  const resolvedOrientation =
    printingMethod === 'offset'
      ? 'portrait'
      : orientation;

  const doc = new jsPDF({
    orientation: resolvedOrientation,
    unit: 'mm',
    format:
      printingMethod === 'offset'
        ? [PDF_WIDTH, PDF_HEIGHT]
        : (
            resolvedOrientation === 'landscape'
              ? [PDF_WIDTH, PDF_HEIGHT]
              : [PDF_HEIGHT, PDF_WIDTH]
          ),
  });

  registerRoboto(doc);

  const pageWidth =
    doc.internal.pageSize.getWidth();

  const pageHeight =
    doc.internal.pageSize.getHeight();

  const usedWidth =
    GRID_COLS * GRID_WIDTH +
    (GRID_COLS - 1) * GAP;

  const usedHeight =
    GRID_ROWS * GRID_HEIGHT +
    (GRID_ROWS - 1) * GAP;

  const marginX =
    (pageWidth - usedWidth) / 2;

  const marginY =
    (pageHeight - usedHeight) / 2;

  let total = 0;

  const counts = {};

  /* ================= CLASSES ================= */

  const classes =
    labels.classStd === 'All'
      ? CLASS_LIST
      : [labels.classStd];

  /* ================= DRAW ================= */

  // Dummy Mode offsets move label content only; grid and border positions stay fixed.
  const contentOffsetX = Number(labels.contentOffsetX || 0);
  const contentOffsetY = Number(labels.contentOffsetY || 0);

  classes.forEach((cls, pageIndex) => {

    if (pageIndex > 0)
      doc.addPage();

    const classKey =
      normalizeClassKey(cls);

    const classImages = artworkImages;

    classImageCounter[classKey] = 0;

    classFontCounter[classKey] = 0;

    let gridIndex = 0;

    for (let i = 0; i < TOTAL_LABELS; i++) {

      const col =
        gridIndex % GRID_COLS;

      const row =
        Math.floor(
          gridIndex / GRID_COLS
        );

      const x =
        marginX + col * GRID_WIDTH;

      const y =
        marginY + row * GRID_HEIGHT;

      /* ================= BORDER ================= */

      if (borderImages.length) {

        const bImg =
          borderImages[
            i % borderImages.length
          ];

        const borderFormat = bImg.startsWith('data:image/png') ? 'PNG' : 'JPEG';
        const borderW = GRID_WIDTH * uploadedScaleX;
        const borderH = GRID_HEIGHT * uploadedScaleY;
        doc.addImage(
          bImg,
          borderFormat,
          x + (GRID_WIDTH - borderW) / 2 + (isDummyMode ? uploadedOffsetX : 0),
          y + (GRID_HEIGHT - borderH) / 2 + (isDummyMode ? uploadedOffsetY : 0),
          borderW,
          borderH
        );

      } else {

        doc.rect(
          x,
          y,
          GRID_WIDTH,
          GRID_HEIGHT
        );
      }

      /* ================= LABEL BLOCK ADJUST ================= */

      const blockAdjust =
        Number(
          labels.labelBlockAdjustment || 0
        );

      // Global movement keeps the complete drawable set together:
      // photo, class artwork, school header and label fields.
      const labelVerticalGap = Number(labels.labelVerticalGap || 0);
      const labelHorizontalGap = Number(labels.labelHorizontalGap || 0);
      const labelLineExtraWidth = Number(labels.labelLineExtraWidth || 0);

      /* ================= CLASS IMAGE ================= */

      if (
        imageMode === 'animation' &&
        classImages.length
      ) {

        const imgData =
          classImages[
            classImageCounter[classKey] % classImages.length
          ];

        classImageCounter[classKey]++;

        const format = imgData.startsWith('data:image/png') ? 'PNG' : 'JPEG';

        doc.addImage(

          imgData,

          format,

          x + 6 + (20 - 20 * uploadedScaleX) / 2 + contentOffsetX + (isDummyMode ? uploadedOffsetX : 0),

          y + 20.5 + blockAdjust + contentOffsetY + (isDummyMode ? uploadedOffsetY : 0),

          20 * uploadedScaleX,

          26 * uploadedScaleY
        );

        doc.setDrawColor(
          128,
          0,
          128
        );

        doc.setLineWidth(0.35);

        doc.roundedRect(
          x + 6 + (20 - 20 * uploadedScaleX) / 2 + contentOffsetX + (isDummyMode ? uploadedOffsetX : 0),
          y + 20.5 + blockAdjust + contentOffsetY + (isDummyMode ? uploadedOffsetY : 0),
          20 * uploadedScaleX,
          26 * uploadedScaleY,
          2.5,
          2.5,
          'S'
        );
      }

      /* ================= UPLOADED PHOTO ================= */

      if (
        labels.uploadedPhotoPreview
      ) {

        const photoFormat =
          labels.uploadedPhotoPreview.startsWith(
            'data:image/png'
          )
            ? 'PNG'
            : 'JPEG';

        const uploadedPhotoW = (labels.pdfPhotoWidth || 8) * (isDummyMode ? uploadedScaleX : 1);
        const uploadedPhotoH = (labels.pdfPhotoHeight || 10) * (isDummyMode ? uploadedScaleY : 1);
        const uploadedPhotoX = x + 6 + (labels.photoOffsetX || 0) + contentOffsetX + (isDummyMode ? uploadedOffsetX : 0);
        const uploadedPhotoY = y + 6 + (labels.photoOffsetY || 0) + contentOffsetY + (isDummyMode ? uploadedOffsetY : 0);

        doc.addImage(
          labels.uploadedPhotoPreview,
          photoFormat,
          uploadedPhotoX,
          uploadedPhotoY,
          uploadedPhotoW,
          uploadedPhotoH,
          undefined,
          'FAST'
        );

        doc.setDrawColor(
          255,
          255,
          255
        );

        doc.setLineWidth(0.5);

        doc.roundedRect(
          uploadedPhotoX,
          uploadedPhotoY,
          uploadedPhotoW,
          uploadedPhotoH,
          1.5,

          1.5,

          'S'
        );
      }

      /* ================= SCHOOL HEADER ================= */

      if (
        isSchoolNameMode &&
        labels.schoolName
      ) {

        const fontStyle =

          labels.schoolNameBold &&
          labels.schoolNameItalic

            ? 'bolditalic'

            : labels.schoolNameBold

            ? 'bold'

            : labels.schoolNameItalic

            ? 'italic'

            : 'normal';

        doc.setFont(
          labels.schoolNameFontStyle,
          fontStyle
        );

        doc.setFontSize(
          labels.schoolNameFontSize
        );

        doc.setTextColor(

          ...hexToRgb(
            labels.schoolNameColor ||
              '#000'
          )
        );

        const schoolBlockX =

          x +
          GRID_WIDTH / 2 +
          (labels.schoolBlockOffsetX || 0) +
          contentOffsetX;

        const schoolBlockY =
          (labels.schoolBlockOffsetY || 0) +
          contentOffsetY;

        const schoolNameToAddress1Gap =
          Number(labels.schoolNameToAddress1Gap || 3.5);

        const address1ToAddress2Gap =
          Number(labels.address1ToAddress2Gap || 3.5);

        const schoolNameY =

          y +
          4 +
          (labels.schoolNameFontSize * 0.45) -
          1;

        /* ================= SCHOOL NAME ================= */

        doc.text(

          labels.schoolName,

          schoolBlockX,

          schoolNameY + schoolBlockY,

          {
            align: 'center',
            maxWidth: GRID_WIDTH - 8,
          }
        );

        /* ================= ADDRESS 1 ================= */

        let addressY =
          schoolNameY + schoolNameToAddress1Gap;

        if (
          labels.schoolAddressLine1?.trim()
        ) {

          const line1FontSize =
            labels.schoolAddressLine1FontSize || 10;

          doc.setFontSize(line1FontSize);

          doc.text(

            labels.schoolAddressLine1,

            schoolBlockX,

            addressY + schoolBlockY,

            {
              align: 'center',
              maxWidth: GRID_WIDTH - 8,
            }
          );

          /* ================= ADDRESS 2 ================= */

          if (
            labels.schoolAddressLine2?.trim()
          ) {

            const line2FontSize =
              labels.schoolAddressLine2FontSize || 9;

            doc.setFontSize(line2FontSize);

            const nextLineY =
              addressY + address1ToAddress2Gap;

            doc.text(

              labels.schoolAddressLine2,

              schoolBlockX,

              nextLineY + schoolBlockY,

              {
                align: 'center',
                maxWidth: GRID_WIDTH - 8,
              }
            );
          }
        }
      }

      /* ================= LABELS ================= */

      // Apply the user's single selected font consistently to all grid writings.
      // Keep the first available font as a safe fallback for older saved settings.
      const gridFont =
        fontStyles.find((f) => f.font === labels.gridFontStyle) ||
        fontStyles[0];

      const labelYOffset =
        isSchoolMode ? -2.5 : 0;

      let textY =
        y + (isDummyMode ? 17 : 22) + labelYOffset + contentOffsetY;

      const textX =
        (imageMode === 'plain'
          ? x + 8
          : x + 27) + contentOffsetX;

      doc.setFont(
        gridFont.font,
        'normal'
      );

      doc.setFontSize(Number(labels.gridFontSize) || gridFont.size);

      /* ================= ROW 1 ================= */

      doc.setTextColor(
        ...hexToRgb(labelColors[0])
      );

      doc.text(
        `${labels.name}:`,
        textX,
        textY
      );

      doc.setDrawColor(
        ...hexToRgb(lineColors[0])
      );

      doc.setLineWidth(0.35);

      doc.line(

        textX + labelHorizontalGap,

        textY + 1.5,

        imageMode === 'plain'
          ? x + GRID_WIDTH - 6 + labelLineExtraWidth
          : textX + GRID_WIDTH - 31 + labelLineExtraWidth,

        textY + 1.5
      );

      textY +=
        (imageMode === 'plain' ? 9 : 8) + labelVerticalGap;

      /* ================= ROW 2 ================= */

      doc.setTextColor(
        ...hexToRgb(labelColors[1])
      );

      doc.text(
        `${labels.study}:`,
        textX,
        textY
      );

      const secX =

        textX +
        doc.getTextWidth(`${labels.study}:`) +
        (imageMode === 'plain' ? 38 : 24);

      if (labels.sec?.trim()) {

        doc.text(
          `${labels.sec}:`,
          secX,
          textY
        );
      }

      doc.setDrawColor(
        ...hexToRgb(lineColors[1])
      );

      doc.line(

        textX + labelHorizontalGap,

        textY + 1.5,

        imageMode === 'plain'
          ? x + GRID_WIDTH - 6 + labelLineExtraWidth
          : textX + GRID_WIDTH - 31 + labelLineExtraWidth,

        textY + 1.5
      );

      textY +=
        (imageMode === 'plain' ? 9 : 8) + labelVerticalGap;

      /* ================= ROW 3 ================= */

      doc.setTextColor(
        ...hexToRgb(labelColors[2])
      );

      doc.text(
        `${labels.rollNo}:`,
        textX,
        textY
      );

      const semX =

        textX +
        doc.getTextWidth(`${labels.rollNo}:`) +
        (imageMode === 'plain' ? 35 : 21);

      if (labels.semester?.trim()) {

        doc.text(
          `${labels.semester}:`,
          semX,
          textY
        );
      }

      doc.setDrawColor(
        ...hexToRgb(lineColors[2])
      );

      doc.line(

        textX + labelHorizontalGap,

        textY + 1.5,

        imageMode === 'plain'
          ? x + GRID_WIDTH - 6 + labelLineExtraWidth
          : textX + GRID_WIDTH - 31 + labelLineExtraWidth,

        textY + 1.5
      );

      textY +=
        (imageMode === 'plain' ? 9 : 8) + labelVerticalGap;

      /* ================= ROW 4 ================= */

      doc.setTextColor(
        ...hexToRgb(labelColors[3])
      );

      doc.text(
        `Subject:`,
        textX,
        textY
      );

      doc.setDrawColor(
        ...hexToRgb(lineColors[3])
      );

      doc.line(

        textX + labelHorizontalGap,

        textY + 1.5,

        imageMode === 'plain'
          ? x + GRID_WIDTH - 6 + labelLineExtraWidth
          : textX + GRID_WIDTH - 31 + labelLineExtraWidth,

        textY + 1.5
      );

      textY +=
        (imageMode === 'plain' ? 9 : 8) + labelVerticalGap;

      /* ================= DUMMY SCHOOL LABEL BELOW SUBJECT ================= */
      if (isDummyMode) {
        // Keep the Dummy School label consistent with the same grid font
        // and font size already applied to every other label in this grid.
        doc.setTextColor(...hexToRgb(labels.dummySchoolNameColor || '#173B67'));
        doc.text(`${String(labels.school || 'School')}:`, textX, textY);
        doc.setDrawColor(...hexToRgb(labels.dummySchoolUnderlineColor || '#D4A017'));
        doc.setLineWidth(0.4);
        // Start the School underline at the same horizontal position as
        // the other grid-field underlines, rather than after the label text.
        doc.line(
          textX + labelHorizontalGap,
          textY + 1.5,
          imageMode === 'plain'
            ? x + GRID_WIDTH - 6 + labelLineExtraWidth
            : textX + GRID_WIDTH - 31 + labelLineExtraWidth,
          textY + 1.5
        );
      }

      /* ================= ROW 5 ================= */

      if (isSchoolMode) {

        doc.setTextColor(
          ...hexToRgb(labelColors[4])
        );

        doc.text(
          `${labels.school}:`,
          textX,
          textY
        );

        doc.setDrawColor(
          ...hexToRgb(lineColors[4])
        );

        doc.line(

          textX + labelHorizontalGap,

          textY + 1.5,

          imageMode === 'plain'
            ? x + GRID_WIDTH - 6 + labelLineExtraWidth
            : textX + GRID_WIDTH - 31 + labelLineExtraWidth,

          textY + 1.5
        );
      }

      gridIndex++;
      total++;
    }

    counts[cls] = TOTAL_LABELS;
  });

  /* ================= SAVE ================= */

  const methodSuffix =
    printingMethod === 'offset'
      ? '_Offset'
      : '_Digital';

  const fileName =

    isSchoolNameMode &&
    labels.schoolName

      ? `${labels.schoolName}${methodSuffix}_Labels.pdf`

      : isLogoMode

      ? `Logo${methodSuffix}_Labels.pdf`

      : `Notebook${methodSuffix}_Labels.pdf`;

  doc.save(fileName);

  setGridCounts(counts);

  setTotalGrids(total);
}